import Vue from 'vue'
import { mapGetters } from 'vuex'
import SubCategoryList from './SubCategoryList/SubCategoryList.vue'
import navigationSkills from '@/mixins/navigationSkills'
import Skill from '@/models-ts/Skill'
import { SubcategoriesWithSkills } from '@/store/shared/modules/skills/types'

interface Data {
  selectedCategoryId: Skill['id'] | null
  selectedMore: boolean
  resizeObserver: ResizeObserver | null
  openTimer: number | null
  closeTimer: number | null
}

export default Vue.extend<any, any, any>({
  mixins: [navigationSkills],
  components: {
    SubCategoryList,
  },
  data(): Data {
    return {
      selectedCategoryId: null,
      selectedMore: false,
      resizeObserver: null,
      openTimer: null,
      closeTimer: null,
    }
  },
  computed: {
    ...mapGetters({
      getSubcategoriesWithSkills: 'skills/getSubcategoriesWithSkills',
      categories: 'skills/getCategories',
    }),
    subcategoriesWithSkillsMap() {
      return this.categories.reduce(
        (acc: Map<Skill['id'], Array<SubcategoriesWithSkills>>, s: Skill) => ({
          ...acc,
          [s.id]: this.getSubcategoriesWithSkills(s.id),
        }),
        {}
      )
    },
    subSkills(): Array<SubcategoriesWithSkills> {
      if (this.selectedCategoryId) {
        return this.subcategoriesWithSkillsMap[this.selectedCategoryId]
      }
      return []
    },
    hasSubSkillsContent() {
      return this.subSkills.some(this.hasSubCategoryContent)
    },
  },
  watch: {
    skillsLoaded() {
      if (this.skillsLoaded && process.client) {
        this.$nextTick(() => {
          this.moveMore()
          this.initObserver()
        })
      }
    },
    '$route.name': {
      handler() {
        this.$nextTick(() => {
          this.moveMore()
        })
      },
    },
  },
  mounted() {
    this.moveMore()
    this.initObserver()
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    this.clearTimers()
  },
  methods: {
    initObserver() {
      if (this.$refs.categories && !this.resizeObserver) {
        this.resizeObserver = new ResizeObserver(this.moveMore)
        this.resizeObserver.observe(this.$refs.categories as HTMLUListElement)
      }
    },
    clearTimers() {
      if (this.openTimer) {
        clearTimeout(this.openTimer)
        this.openTimer = null
      }
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    onSelectCategory(category: Skill | null) {
      // Clear any pending close
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }

      // Clear any pending open
      if (this.openTimer) {
        clearTimeout(this.openTimer)
      }

      // Delay opening panel to avoid accidental hovers
      this.openTimer = window.setTimeout(() => {
        if (category && !this.isMoreCategory(category)) {
          this.selectedMore = false
        }
        this.selectedCategoryId = category?.id
        this.openTimer = null
      }, 300) // 300ms delay before opening
    },
    onSelectMore() {
      // Clear any pending close
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }

      // Clear any pending open
      if (this.openTimer) {
        clearTimeout(this.openTimer)
      }

      // Delay opening More panel
      this.openTimer = window.setTimeout(() => {
        this.selectedMore = true
        this.selectedCategoryId = this.sortedCategories.find((skill: Skill) => this.isMoreCategory(skill) && this.hasContent(skill))?.id
        this.openTimer = null
      }, 300)
    },
    cancelClose() {
      // Cancel any pending close when mouse returns to menu
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    hidePanel() {
      // Clear any pending open
      if (this.openTimer) {
        clearTimeout(this.openTimer)
        this.openTimer = null
      }

      // Delay closing to allow moving between menu items
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
      }

      this.closeTimer = window.setTimeout(() => {
        this.selectedCategoryId = null
        this.selectedMore = false
        this.closeTimer = null
      }, 200) // 200ms delay before closing
    },
    hidePanelImmediately() {
      // Immediate close when clicking on a link
      this.clearTimers()
      this.selectedCategoryId = null
      this.selectedMore = false
    },
    isSelected(category: Skill) {
      return category.id === this.selectedCategoryId
    },
    isMoreCategory(category: Skill) {
      const categories = this.$refs.categories as HTMLUListElement
      if (categories) {
        const categoryEl = Array.from(categories.children).find((el) => el.getAttribute('data-name') === category.name) as HTMLLIElement
        if (categoryEl) {
          return categoryEl.offsetTop > 8
        }
      }
      return false
    },
    moveMore() {
      const categoriesList = this.$refs.categories as HTMLUListElement
      const more = this.$refs['more-category'] as HTMLUListElement
      const gap = 34
      if (categoriesList) {
        const categories = Array.from(categoriesList.children) as Array<HTMLLIElement>
        if (categories.length) {
          let lastVisibleOffset = categories[0].offsetLeft + categories[0].clientWidth
          for (const el of categories) {
            if (el.offsetTop < 9) {
              lastVisibleOffset = el.offsetLeft + el.clientWidth
            } else {
              break
            }
          }
          more.style.left = `${lastVisibleOffset + gap}px`
        }
      }
      return false
    },
  },
})
